import { StepsButtons } from "@/components/button/steps-button";
import { CloseIcon } from "@/components/icons/close-icon";
import { PlusIcon } from "@/components/icons/plus-icon";
import { SearchIcon } from "@/components/icons/search-icon";
import { FilledButton } from "@/components/ui/buttons";
import { PillButton } from "@/components/ui/buttons/PillButton";
import { useFoodAvoids } from "@/hooks/features/use-food-avoids";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText, useIonRouter } from "@ionic/react";
import { useRef, useState } from "react";
import { AnimatedOnboardingContainer } from "../../components/animated-onboarding-container";
import { SearchDislikeFoodModal } from "../../components/search-dislike-food-modal";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const OnboardingFoodAvoids = () => {
  const pageRef = useRef<HTMLElement>(null);
  const { info } = useStepRouteInfo();
  const navigation = useIonRouter();
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const {
    sortedFoodList,
    selectedFoods,
    setSelectedFoods,
    isUpdating,
    isLoading,
    addCustomFood,
    removeCustomFood,
    handleSubmit,
    customFoods,
  } = useFoodAvoids(() => {
    setSmartlookOnboardingEvent({
      type: "submit",
      stepPath: info?.path,
    });
    setMixpanelOnboardingEvent({
      type: "submit",
      stepPath: info?.path,
    });
    navigation.push(info?.nextPath ?? "", "none");
  });

  const toggleFoodOption = (food: string) => {
    const isCustom = customFoods.includes(food);
    if (isCustom) {
      removeCustomFood(food);
      return;
    }

    setSelectedFoods((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(food)) {
        newSet.delete(food);
      } else {
        newSet.add(food);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <AnimatedOnboardingContainer
        title="Tell me what you'd like me to avoid."
        pageRef={pageRef}
        handleSubmit={handleSubmit}
      >
        <div className="flex w-full flex-1 flex-col gap-[18px] overflow-y-auto py-9">
          <div className="flex flex-wrap gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <IonSkeletonText key={i} animated className="h-[38px] w-24 rounded-full" />
            ))}
          </div>
        </div>
        <StepsButtons type="submit" disabled={true} />
      </AnimatedOnboardingContainer>
    );
  }

  return (
    <AnimatedOnboardingContainer
      title="Tell me what you'd like me to avoid."
      pageRef={pageRef}
      handleSubmit={handleSubmit}
      classNames={{
        container: "flex-1 h-full flex flex-col",
        form: "flex-1 flex flex-col overflow-y-scroll no-scrollbar gap-8",
      }}
    >
      <div className="flex w-full flex-1 flex-col justify-center gap-[18px]">
        <div className="flex w-full flex-1 flex-col justify-center gap-[18px]">
          <div className="flex flex-wrap content-start gap-3">
            {sortedFoodList.map((food) => {
              const isSelected = selectedFoods.has(food);
              const isCustom = customFoods.includes(food);
              const iconClass = isSelected
                ? "w-4 h-4 !text-white !fill-white relative inset-0 scale-150"
                : "w-4 h-4 !text-black !fill-black";

              return (
                <PillButton
                  key={food}
                  selected={isSelected}
                  onClick={() => toggleFoodOption(food)}
                  disabled={isUpdating}
                  className="flex items-center gap-2"
                >
                  {food}
                  {isCustom || isSelected ? (
                    <CloseIcon className={cn(iconClass)} />
                  ) : (
                    <PlusIcon className={iconClass} />
                  )}
                </PillButton>
              );
            })}
          </div>

          <FilledButton
            type="button"
            color="black"
            className="w-full justify-start gap-2 px-5"
            textCase="normal"
            disabled={isUpdating}
            onClick={() => setIsSearchModalOpen(true)}
          >
            <SearchIcon className="h-6 w-6 fill-white" />
            <span className={cn(textVariants.footnote)}>Want to add more? Search here.</span>
          </FilledButton>
        </div>
      </div>

      <StepsButtons type="button" disabled={isUpdating} onClick={handleSubmit} />

      <SearchDislikeFoodModal
        pageRef={pageRef}
        isOpen={isSearchModalOpen}
        onDismiss={() => setIsSearchModalOpen(false)}
        onSave={(foods) => {
          foods.forEach((food) => addCustomFood(food));
        }}
      />
    </AnimatedOnboardingContainer>
  );
};

export default OnboardingFoodAvoids;
